import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem("brimi_user");
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("brimi_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("brimi_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("brimi_token");
        localStorage.removeItem("brimi_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
      localStorage.setItem("brimi_user", JSON.stringify(r.data));
      return r.data;
    } catch {
      return null;
    }
  }, []);

  const login = useCallback(
    async (email, password) => {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("brimi_token", data.token);
      localStorage.setItem("brimi_user", JSON.stringify(data.user));
      setUser(data.user);
      // Pull full /auth/me so user.access is populated for header badges + gating UI.
      await refreshUser();
      return data.user;
    },
    [refreshUser],
  );

  const signup = useCallback(
    async (name, email, password) => {
      const { data } = await api.post("/auth/signup", { name, email, password });
      localStorage.setItem("brimi_token", data.token);
      localStorage.setItem("brimi_user", JSON.stringify(data.user));
      setUser(data.user);
      await refreshUser();
      return data.user;
    },
    [refreshUser],
  );

  // Called by /auth/callback after Emergent Google Auth returns a Brimi JWT.
  const applyGoogleSession = useCallback(
    async (payload) => {
      localStorage.setItem("brimi_token", payload.token);
      localStorage.setItem("brimi_user", JSON.stringify(payload.user));
      setUser(payload.user);
      await refreshUser();
      return payload.user;
    },
    [refreshUser],
  );

  const logout = useCallback(() => {
    localStorage.removeItem("brimi_token");
    localStorage.removeItem("brimi_user");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, logout, refreshUser, applyGoogleSession }),
    [user, loading, login, signup, logout, refreshUser, applyGoogleSession],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
